import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Box, HStack } from '@chakra-ui/react';

import DropdownMenu from '../../../../../../components/DropdownMenu';
import FormInput from '../../../../../../components/form/FormInput';
import { inputTypes } from '../../../../../../components/form/FormInput/util';
import InputLabel from '../../../../../../components/form/InputLabel';
import RequirementTemplateDropdownMenu from '../../../../../../components/RequirementTemplateDropdownMenu';
import RQSearchableDropdownMenu from '../../../../../../components/RQSearchableDropdownMenu';
import LoadingSpinner from '../../../../../../components/Spinner';
import { analyticsIds } from '../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../constants/inAppText';
import { selectJobProfile, updateCurrentDepartment, updateCurrentOwner } from '../../../../../../redux/modules/jobs';
import { useCompanyActiveUsers } from '../../../../../../services/company-service';
import UseFormMethodsType from '../../../../../../types/UseFormMethodsType';
import { activeDepartments } from '../../../../../../utils/departmentUtil';
import { validateDates } from '../../../../../JobsPage/components/CreateJobForm/util';
import { formInputNames } from '../../util';

const JobInfoForm = ({ useFormMethods }) => {
  const { companyId } = useParams();
  const dispatch = useDispatch();
  const jobProfile = useSelector(selectJobProfile);

  const companyUserReactQuery = useCompanyActiveUsers(companyId);

  const { setValue } = useFormMethods;

  const {
    currentRequirements,
    currentOwner,
    department,
    departmentOptions
  } = jobProfile;

  const handleOptionClick = (selected, type) => {
    switch (type) {
      case 'owner': {
        dispatch(updateCurrentOwner(selected));
        break;
      }

      case 'department': {
        dispatch(updateCurrentDepartment(selected));
        break;
      }

      default:
        break;
    }
  };

  if (!department) return <LoadingSpinner isInline />;

  return (
    <>
      <FormInput
        required
        cypressId={CypressIds.projectNameFormInput}
        dataAnalyticsId={analyticsIds.projectDetailsPage.editProjectForm.name}
        inputName={formInputNames.jobName}
        labelName="Project Name"
        placeholder="Project Name"
        type={inputTypes.standard}
        useFormMethods={useFormMethods}
        validationRules={{
          required: {
            value: true,
            message: 'Project name is required'
          },
          maxLength: {
            value: 250,
            message: 'Project name must be fewer than 250 characters'
          },
          minLength: {
            value: 3,
            message: 'Project name must have at least 3 characters'
          }
        }}
      />

      <FormInput
        dataAnalyticsId={analyticsIds.projectDetailsPage.editProjectForm.number}
        inputName={formInputNames.jobNumber}
        labelName="Project Number"
        placeholder="Project Number"
        type={inputTypes.standard}
        useFormMethods={useFormMethods}
        validationRules={{
          maxLength: 75,
          message: 'Project number must have fewer than 75 characters'
        }}
      />

      <FormInput
        dataAnalyticsId={analyticsIds.projectDetailsPage.editProjectForm.description}
        inputName={formInputNames.jobDescription}
        labelName="Project Description"
        placeholder="Project Description"
        type={inputTypes.textArea}
        useFormMethods={useFormMethods}
      />

      <HStack alignItems="flex-end" justifyContent="space-between" w="100%">
        <FormInput
          disableEnterKey
          inputName={formInputNames.jobStart}
          key={formInputNames.jobStart}
          labelName="Start Date"
          maxDate={useFormMethods.watch(formInputNames.jobEnd)}
          placeholder="MM/DD/YYYY"
          required={false}
          type="date"
          useFormMethods={useFormMethods}
          validationRules={{ validate: value => validateDates(value, formInputNames.jobStart, useFormMethods) }}
        />
        <FormInput
          disableEnterKey
          inputName={formInputNames.jobEnd}
          key={formInputNames.jobEnd}
          labelName="End Date"
          minDate={useFormMethods.watch(formInputNames.jobStart)}
          placeholder="MM/DD/YYYY"
          required={false}
          type="date"
          useFormMethods={useFormMethods}
          validationRules={{ validate: value => validateDates(value, formInputNames.jobEnd, useFormMethods) }}
        />
      </HStack>

      <Box mb={6} mt="0.3rem">
        <InputLabel
          labelName="Project Owner"
          toolTip={inAppText.tooltips.projectDetails.projectInformation.projectOwner}
        />
        <RQSearchableDropdownMenu
          dataAnalyticsKey={analyticsIds.projectDetailsPage.editProjectForm.ownerDropdown}
          getLabel={i => `${i.userFirstName} ${i.userLastName}`}
          idKey="user_id"
          reactQuery={companyUserReactQuery}
          searchKey="userFirstName"
          selected={currentOwner}
          width="332px"
          onCancel={() => handleOptionClick(null, 'owner')}
          onSelect={owner => handleOptionClick(owner, 'owner')}
        />
      </Box>
      <Box my={6}>
        <InputLabel
          labelName="Department"
          toolTip="The department should this job belong to"
        />
        <DropdownMenu
          useOriginal
          dataAnalyticsKey={analyticsIds.projectDetailsPage.editProjectForm.departmentDropdown}
          labelKey="label"
          options={activeDepartments(departmentOptions)}
          placeholder="No department selected"
          selected={department.label || department.departmentName}
          variant="primary"
          width="332px"
          onSelect={d => handleOptionClick(d, 'department')}
        />
      </Box>

      <Box my={6}>
        <InputLabel
          labelName="Default Insurance Requirements"
          toolTip="Select the default requirement template to use anytime a record is created for this project"
        />
        <RequirementTemplateDropdownMenu
          companyId={companyId}
          defaultTemplate={currentRequirements}
          variant="primary"
          onChange={value => setValue('default_requirement_template_id', value?.id ?? null)}
        />
      </Box>
    </>
  );
};

JobInfoForm.propTypes = { useFormMethods: UseFormMethodsType };

export default JobInfoForm;
